/**
 * `break_allowance`: the shift has less break than the rule requires for its
 * length. The fix is simply to give the guard the missing break, placed
 * mid-shift and staggered around colleagues on the same post.
 */

import { findViolationShift, numberDetail } from '../rosterModel';
import type { ProposedFix, RemediationStrategy, StrategyResult } from '../types';
import { planOwedBreak } from './breakCoordination';

export const breakAllowanceStrategy: RemediationStrategy = {
  ruleCodes: ['break_allowance'],

  async propose(violation, context): Promise<StrategyResult> {
    const shift = findViolationShift(violation, context.snapshot);
    if (!shift || !violation.guard_id) return { proposals: [], unsupportedReason: 'shift_not_on_roster' };

    const plan = planOwedBreak(shift, context, numberDetail(violation, 'required_break_minutes'));
    if (plan.owedMinutes <= 0) return { proposals: [] };

    const base = {
      guardId: shift.guardId,
      guardName: shift.guardName || violation.guard_name || '',
      newBoundaryMinute: null,
      candidate: null,
      uncoveredMinutes: 0,
    };
    const proposals: ProposedFix[] = [];

    // A short break topped up elsewhere adds up on paper, but some rules (GB's
    // 20 minutes) want it uninterrupted — so lead with one continuous break.
    if (plan.replacement) {
      proposals.push({
        ...base,
        kind: 'replace_break',
        window: plan.replacement,
        preference: 0,
        operations: [
          ...shift.breaks.map((breakSlot) => ({ op: 'remove_break' as const, break_slot_id: breakSlot.id, guard_id: shift.guardId })),
          {
            op: 'add_break',
            guard_id: shift.guardId,
            start_minute: plan.replacement.startMinute,
            end_minute: plan.replacement.endMinute,
            is_paid: shift.breaks.some((breakSlot) => breakSlot.is_paid),
          },
        ],
      });
    }

    if (plan.topUp) {
      proposals.push({
        ...base,
        kind: 'add_break',
        window: plan.topUp,
        preference: plan.replacement ? 1 : 0,
        operations: [{
          op: 'add_break',
          guard_id: shift.guardId,
          start_minute: plan.topUp.startMinute,
          end_minute: plan.topUp.endMinute,
          is_paid: false,
        }],
      });
    }

    // No room for the break inside the roster day (e.g. it all falls after midnight).
    return proposals.length > 0 ? { proposals } : { proposals, unsupportedReason: 'no_verified_fix' };
  },
};
